import React, { useState, useEffect, useCallback } from "react";

const SearchModal = ({ onClose }) => {
  const [reservations, setReservations] = useState([]);
  const [search, setSearch] = useState("");
  const [tableFilter, setTableFilter] = useState("");
  const [dateFilter, setDateFilter] = useState("");
  const [allTables, setAllTables] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});
  const [loading, setLoading] = useState(false);
  const [freeTables, setFreeTables] = useState([]);
  const [confirmDelete, setConfirmDelete] = useState(null); // čuva id rezervacije za brisanje

  const fetchReservations = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (search) params.append("search", search);
    if (tableFilter) params.append("table", tableFilter);
    if (dateFilter) params.append("date", dateFilter);

    fetch(`/reservations/api/reservations/all/?${params}`)
      .then(res => res.json())
      .then(data => {
        setReservations(data.reservations || []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [search, tableFilter, dateFilter]);

  // Učitaj sve stolove za filter dropdown
  useEffect(() => {
    fetch("/reservations/api/tables/")
      .then(res => res.json())
      .then(data => setAllTables(data.tables || []));
  }, []);

  useEffect(() => {
    fetchReservations();
  }, [fetchReservations]);

  useEffect(() => {
    if (!editingId) return;

    const r = reservations.find(r => r.id === editingId);
    if (!r) return;

    const params = new URLSearchParams({
      date: r.reservation_date_raw,
      start: r.start_datetime,
      end: r.end_datetime,
      exclude_id: r.id,
    });

    fetch(`/reservations/api/tables/free-for-slot/?${params}`)
      .then(res => res.json())
      .then(data => setFreeTables(data.tables || []));
  }, [editingId, reservations]); 

  
  const handleDelete = async (id) => {
    const result = await new Promise((resolve) => {
      setConfirmDelete({ id, resolve });
    });

    if (result) {
      await fetch(`/reservations/api/reservations/${id}/delete/`, { method: "DELETE" });
      fetchReservations();
    }
    setConfirmDelete(null);
  };

  const handleEditStart = (r) => {
    setEditingId(r.id);
    setEditData({
      guest_name: r.guest_name,
      guests: r.guests,
      note: r.note,
      table_label: r.tables[0] || "",  // ✅ trenutni stol
    });
  };

  const handleEditSave = async (id) => {
    await fetch(`/reservations/api/reservations/${id}/update/`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(editData),
    });
    setEditingId(null);
    fetchReservations();
  };

  const handleKeyDown = (e, id) => {
    if (e.key === "Enter") handleEditSave(id);
  };

  return (
    <div
      id="searchModalBackground"
      className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50"
    >
      <div className="bg-gray-900 border border-[#c1b428] rounded-xl shadow-2xl w-[800px] max-h-[85vh] flex flex-col">

        {/* Header */}
        <div className="p-4 border-b border-[#c1b428]">
          <h2 className="text-base font-bold text-center text-[#c1b428] mb-3">
            Pretraga rezervacija
          </h2>

          {/* Pretraga i filteri */}
          <div className="flex gap-2">
            
            <div className="flex-1 relative">
              <input
                type="text"
                placeholder="Pretraži po imenu gosta..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full bg-gray-800 text-white border border-gray-600 rounded px-3 py-1.5 text-xs pr-6"
              />
              {search && (
                <button
                  onClick={() => setSearch("")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white text-xs"
                >
                  ✕
                </button>
              )}
            </div>

            <select
              value={tableFilter}
              onChange={(e) => setTableFilter(e.target.value)}
              className="bg-gray-800 text-[#c1b428] border border-gray-600 rounded px-2 py-1.5 text-xs"
            >
              <option value="">Svi stolovi</option>
              {allTables.map(t => (
                <option key={t.id} value={t.label}>{t.label}</option>
              ))}
            </select>
            <input
              type="date"
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className="bg-gray-800 text-[#c1b428] border border-gray-600 rounded px-2 py-1.5 text-xs"
            />
          </div>
        </div>

        {/* Lista */}
        <div className="overflow-y-auto flex-1 p-3 space-y-2">
          {loading ? (
            <p className="text-center text-gray-400 py-8">Učitavam...</p>
          ) : reservations.length > 0 ? (
            reservations.map((r) => {
              const now = new Date();
              const todayStr = now.toLocaleDateString("en-CA");
              const isToday = r.reservation_date_raw === todayStr;

              let isPast = false;
              if (isToday && r.start_datetime) {
                const [h, m] = r.start_datetime.split(":").map(Number);
                isPast = (h * 60 + m) < (now.getHours() * 60 + now.getMinutes());
              }

              return (
                <div
                  key={r.id}
                  className={`border rounded-lg p-3 text-xs ${
                    isPast && editingId !== r.id
                      ? "bg-gray-900 border-gray-700 opacity-40"
                      : "bg-gray-800 border-gray-700"
                  }`}
                >
                  {editingId === r.id ? (
                    // Edit mode
                    <div className="space-y-2">
                      <div className="flex gap-2">
                        <div className="flex-1">
                          <label className="text-[#c1b428] text-xs">Ime gosta:</label>
                          <input
                            type="text"
                            value={editData.guest_name}
                            onChange={(e) => setEditData({ ...editData, guest_name: e.target.value })}
                            onKeyDown={(e) => handleKeyDown(e, r.id)} 
                            className="w-full bg-gray-700 text-white border border-gray-600 rounded px-2 py-1 text-xs mt-0.5"
                          />
                        </div>
                        <div className="w-20">
                          <label className="text-[#c1b428] text-xs">Osoba:</label>
                          <input
                            type="number"
                            value={editData.guests}
                            onChange={(e) => setEditData({ ...editData, guests: Number(e.target.value) })}
                            onKeyDown={(e) => handleKeyDown(e, r.id)} 
                            className="w-full bg-gray-700 text-white border border-gray-600 rounded px-2 py-1 text-xs mt-0.5"
                            min="1"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="text-[#c1b428] text-xs">Stol:</label>
                        <select
                          value={editData.table_label}
                          onChange={(e) => setEditData({ ...editData, table_label: e.target.value })}
                          onKeyDown={(e) => handleKeyDown(e, r.id)} 
                          className="w-full bg-gray-700 text-[#c1b428] border border-gray-600 rounded px-2 py-1 text-xs mt-0.5"
                        >
                          {reservations.find(res => res.id === editingId)?.tables.map(t => (
                            <option key={t} value={t}>{t} (trenutni)</option>
                          ))}
                          {freeTables
                            .filter(t => !reservations.find(res => res.id === editingId)?.tables.includes(t.label))
                            .map(t => (
                              <option key={t.id} value={t.label}>{t.label} ({t.seats} mjesta)</option>
                            ))
                          }
                        </select>
                      </div>
                      <div>
                        <label className="text-[#c1b428] text-xs">Napomena:</label>
                        <input
                          type="text"
                          value={editData.note}
                          onChange={(e) => setEditData({ ...editData, note: e.target.value })}
                          onKeyDown={(e) => handleKeyDown(e, r.id)}
                          className="w-full bg-gray-700 text-white border border-gray-600 rounded px-2 py-1 text-xs mt-0.5"
                        />
                      </div>
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => setEditingId(null)}
                          className="px-3 py-1 bg-gray-600 text-white rounded text-xs hover:bg-gray-500"
                        >
                          Odustani
                        </button>
                        <button
                          onClick={() => handleEditSave(r.id)}
                          className="px-3 py-1 bg-[#c1b428] text-black rounded text-xs font-bold hover:bg-yellow-500"
                        >
                          Spremi
                        </button>
                      </div>
                    </div>
                  ) : (
                    // View mode
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`font-bold px-2 py-0.5 rounded text-xs ${
                            isPast ? "bg-gray-600 text-gray-300" : "bg-[#c1b428] text-black"
                          }`}>
                            {r.tables.join(", ")}
                          </span>
                          <span className="text-gray-400">{r.reservation_date}</span>
                          <span className="text-gray-300 font-semibold">
                            {r.start_datetime} – {r.end_datetime}
                            {r.shift && <span className="ml-1 text-[#c1b428]">({r.shift})</span>}
                          </span>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-white font-semibold">{r.guest_name}</span>
                          <span className="text-gray-400">👥 {r.guests}</span>
                          {r.note && <span className="text-gray-400 italic">📝 {r.note}</span>}
                        </div>
                      </div>
                      <div className="flex gap-1 ml-2">
                        <button
                          onClick={() => handleEditStart(r)}
                          className="px-2 py-1 bg-gray-700 text-[#c1b428] rounded text-xs hover:bg-gray-600"
                          title="Uredi"
                        >
                          ✏️
                        </button>
                        <button
                          onClick={() => handleDelete(r.id)}
                          className="px-2 py-1 bg-gray-700 text-red-400 rounded text-xs hover:bg-red-900"
                          title="Obriši"
                        >
                          🗑️
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          ) : (
            <p className="text-center text-gray-400 py-8">Nema rezervacija.</p>
          )}
        </div>

        {/* Footer */}
        <div className="p-3 border-t border-[#c1b428] flex justify-between items-center">
          <span className="text-xs text-gray-400">{reservations.length} rezervacija</span>
          <button
            onClick={onClose}
            className="px-4 py-1.5 bg-gray-700 text-[#c1b428] rounded hover:bg-gray-600 text-xs font-semibold"
          >
            Zatvori
          </button>
        </div>

      </div>
    
      {/* Confirm dialog za brisanje */}
      {confirmDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-[60]">
          <div className="bg-gray-900 border border-[#c1b428] rounded-xl shadow-2xl p-6 w-80 text-center">
            <div className="text-3xl mb-3">🗑️</div>
            <h3 className="text-[#c1b428] font-bold text-sm mb-2">Brisanje rezervacije</h3>
            <p className="text-gray-300 text-xs mb-5">
              Jesi li siguran da želiš obrisati ovu rezervaciju? Ova radnja se ne može poništiti.
            </p>
            <div className="flex gap-3 justify-center">
              <button
                onClick={() => confirmDelete.resolve(false)}
                className="px-4 py-2 bg-gray-700 text-[#c1b428] rounded hover:bg-gray-600 text-xs font-semibold"
              >
                Odustani
              </button>
              <button
                onClick={() => confirmDelete.resolve(true)}
                className="px-4 py-2 bg-red-700 text-white rounded hover:bg-red-600 text-xs font-bold"
              >
                Obriši
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default SearchModal;